import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import { NutritionEntitySyncStatus } from "../../Firestore/nutrition/OptimisticNutritionEntity";

const NutritionSyncNotice = observer(function NutritionSyncNotice({
  session,
  onReload = null,
  onRetry = null,
  tone = null,
  title = null,
  message = null,
  actionLabel = null,
  onAction = null,
  style = null,
}) {
  const { t } = useTranslation();
  const shouldUseExplicitCopy =
    typeof message === "string" && message.trim().length > 0;
  const shouldShowRemoteDelete =
    !shouldUseExplicitCopy && session?.deletedRemotely === true;
  const shouldRetry =
    !shouldUseExplicitCopy &&
    !shouldShowRemoteDelete &&
    Boolean(session?.lastError) &&
    (session?.syncStatus ?? session?.status) !== NutritionEntitySyncStatus.OFFLINE_PENDING;

  if (!shouldUseExplicitCopy && !shouldShowRemoteDelete && !shouldRetry) {
    return null;
  }

  const resolvedTone = shouldUseExplicitCopy
    ? tone ?? "warning"
    : shouldShowRemoteDelete
      ? "warning"
      : "error";
  const resolvedTitle = shouldUseExplicitCopy
    ? title
    : shouldShowRemoteDelete
      ? t(
          "nutritionDeletedRemotelyTitle-string",
          "Deleted elsewhere",
        )
      : t("autosaveError-string");
  const resolvedMessage = shouldUseExplicitCopy
    ? message
    : shouldShowRemoteDelete
      ? t(
          "nutritionDeletedRemotelyMessage-string",
          "This item was deleted elsewhere. Saving your local changes will restore it, or discarding will remove it locally.",
        )
      : t(
          "nutritionSaveRetryNotice-string",
          "The last save failed. Retry the save to keep this draft in sync.",
        );
  const resolvedActionLabel = shouldUseExplicitCopy
    ? actionLabel
    : shouldShowRemoteDelete
      ? t("save-string")
      : t("nutritionRetrySave-string", "Retry save");
  const resolvedAction = shouldUseExplicitCopy
    ? onAction
    : shouldShowRemoteDelete
      ? (typeof onRetry === "function" ? onRetry : session?.saveNow?.bind(session))
      : onRetry;

  return (
    <View
      style={[
        styles.notice,
        resolvedTone === "error" ? styles.noticeError : styles.noticeAttention,
        style,
      ]}
    >
      <View style={styles.copyWrap}>
        {resolvedTitle ? <Text style={styles.title}>{resolvedTitle}</Text> : null}
        <Text style={styles.message}>{resolvedMessage}</Text>
      </View>
      {typeof resolvedAction === "function" ? (
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={resolvedAction}
          style={styles.actionButton}
        >
          <Text style={styles.actionText}>{resolvedActionLabel}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  notice: {
    borderRadius: 12,
    borderWidth: 0.5,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  copyWrap: {
    flex: 1,
    gap: 2,
  },
  noticeAttention: {
    backgroundColor: "rgba(194, 65, 12, 0.08)",
    borderColor: "rgba(194, 65, 12, 0.18)",
  },
  noticeError: {
    backgroundColor: "rgba(240, 68, 56, 0.08)",
    borderColor: "rgba(240, 68, 56, 0.18)",
  },
  title: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  message: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.darkgrayColor,
  },
  actionButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Colors.whiteColor,
  },
  actionText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
});

export default NutritionSyncNotice;
